import { effect } from "@preact/signals";
import { getMe, isApiError, errorMessage, logout, setUnauthorizedHandler } from "../api/client";
import type { FullAuthResponse } from "../api/types";
import { applyAuth, clearAuth, me, setMe, token } from "../state/auth";
import { bootError, bootStatus } from "../state/boot";
import { gameStatus } from "../state/game";
import { meta, loadMeta } from "../state/meta";
import { resetTo } from "../state/route";

const FONT_WAIT_MS = 1500;

/** Wait (bounded) for the UI fonts so the first paint does not reflow when they swap in. */
async function fontsReady(): Promise<void> {
  const loads = Promise.all([
    document.fonts.load("500 1em 'Barlow Condensed'"),
    document.fonts.load("800 1em 'Barlow Condensed'"),
    document.fonts.load("400 1em Inter"),
    document.fonts.load("600 1em Inter"),
  ]);
  const { promise: timeout, resolve } = Promise.withResolvers<void>();
  window.setTimeout(resolve, FONT_WAIT_MS);
  await Promise.race([loads, timeout]).catch(() => undefined);
}

/** Load the catalog and show the menus. */
async function finishBoot(): Promise<void> {
  try {
    await Promise.all([meta.value ? Promise.resolve() : loadMeta(), fontsReady()]);
    bootStatus.value = "ready";
  } catch (e) {
    bootError.value = errorMessage(e);
    bootStatus.value = "error";
  }
}

/**
 * Boot sequence: restore the session from the stored token (GET /api/me), otherwise ask for a
 * name (first run or expired session), then load the catalog. Safe to call again for "Retry".
 */
export async function runBoot(): Promise<void> {
  bootStatus.value = "loading";
  bootError.value = "";
  if (token.value && !me.value) {
    try {
      setMe(await getMe());
    } catch (e) {
      if (isApiError(e) && e.status === 401) {
        clearAuth();
      } else {
        bootError.value = errorMessage(e);
        bootStatus.value = "error";
        return;
      }
    }
  }
  if (!me.value) {
    bootStatus.value = "name";
    return;
  }
  await finishBoot();
}

/** Called by the first-run prompt after a guest account was created or a login succeeded. */
export async function completeSignIn(res: FullAuthResponse): Promise<void> {
  applyAuth(res);
  bootStatus.value = "loading";
  await finishBoot();
}

/** Revoke the session (best effort) and return to the first-run prompt. */
export async function signOut(): Promise<void> {
  try {
    await logout();
  } catch {
    // The server may already have dropped the session; the local state is cleared regardless.
  }
  clearAuth();
  resetTo("main");
}

/**
 * Wire app-wide reactions: an expired session clears the account, and whenever the account is
 * gone and no match is running the first-run prompt is shown again. Browser "online" retries a
 * failed boot.
 */
export function installBootEffects(): void {
  setUnauthorizedHandler(clearAuth);
  effect(() => {
    if (bootStatus.value === "ready" && me.value === null && gameStatus.value === "idle") {
      resetTo("main");
      bootStatus.value = "name";
    }
  });
  window.addEventListener("online", () => {
    if (bootStatus.value === "error") void runBoot();
  });
}
